import { useCallback, useEffect } from 'react';

const { legalPlays } = require('../../../shared/game/belot/armenian/tricks');
const { detectCombinations, declarationComboKey } = require('../../../shared/game/belot/armenian/declarations');
const { pickBotAuctionCommand } = require('../../../game/belotArmenian/botAuction');
const {
  pickBotPlayCmd: buildBotPlayCmd,
  pickBotPlayCard,
} = require('../../../game/belotArmenian/botPlay');

const HUMAN_SEAT = 0;
const BOT_TURN_MS = 880;

export function useBelotArmenianBots({
  botPlayMode,
  sessionStarted,
  engineState,
  trickAnim,
  stateRef,
  roundDealAnimActive,
  pendingPlayCmdRef,
  setOppTurnClock,
  setOppRingTick,
  runBotPlayCommand,
  apply,
}) {
  const pickPlayCmdForSeat = useCallback((state, seat) => {
    const c = state.contract;
    const hand = state.hands?.[seat];
    const leadSeat = state.trickLeadSeat;
    if (!c || !hand || leadSeat == null) return null;
    const legal = legalPlays(hand, state.currentTrick, c.trumpSuit, leadSeat);
    if (!legal.length) return null;
    const card = pickBotPlayCard(state, seat, legal, 'simple');
    if (card == null || !Number.isInteger(card)) return null;
    const payload = { card };
    if (
      state.declarationMode === 'hardcore' &&
      !state.firstPlayDeclarationsCommitted?.[seat] &&
      Array.isArray(state.handsAtRoundStart?.[seat]) &&
      state.handsAtRoundStart[seat].length === 8
    ) {
      try {
        if (detectCombinations(state.handsAtRoundStart[seat]).length > 0) {
          payload.commitDeclaredKeys = [];
        }
      } catch {
        /* ignore */
      }
    }
    return { type: 'PLAY_CARD', seat, payload };
  }, []);

  const pickBotPlayCmd = useCallback(
    (state) => {
      const seat = state.currentTurnSeat;
      if (seat == null || seat === HUMAN_SEAT) return null;
      const c = state.contract;
      const hand = state.hands?.[seat];
      const leadSeat = state.trickLeadSeat;
      if (!c || !hand || leadSeat == null) return null;
      const legal = legalPlays(hand, state.currentTrick, c.trumpSuit, leadSeat);
      if (!legal.length) return null;
      let cmd = buildBotPlayCmd(state, seat, legal, botPlayMode);
      if (!cmd) return null;
      if (
        state.declarationMode === 'hardcore' &&
        !state.firstPlayDeclarationsCommitted?.[seat] &&
        Array.isArray(state.handsAtRoundStart?.[seat]) &&
        state.handsAtRoundStart[seat].length === 8
      ) {
        try {
          const combos = detectCombinations(state.handsAtRoundStart[seat]);
          if (combos.length > 0) {
            const sorted = [...combos].sort(
              (a, b) => (Number(b.points) || 0) - (Number(a.points) || 0),
            );
            let chosen = sorted.filter((c) => (Number(c.points) || 0) >= 20);
            if (chosen.length > 2) chosen = chosen.slice(0, 2);
            if (chosen.length === 0) chosen = sorted.slice(0, 1);
            cmd = {
              ...cmd,
              payload: {
                ...cmd.payload,
                commitDeclaredKeys: chosen.map(declarationComboKey),
              },
            };
          }
        } catch {
          /* ignore */
        }
      }
      return cmd;
    },
    [botPlayMode],
  );

  useEffect(() => {
    if (!sessionStarted) {
      setOppTurnClock(null);
      return undefined;
    }
    if (trickAnim && (trickAnim.phase === 'reveal' || trickAnim.phase === 'collect')) {
      setOppTurnClock(null);
      return undefined;
    }
    const seat = engineState.currentTurnSeat;
    const botTurn =
      (engineState.phase === 'auction' || engineState.phase === 'play') &&
      seat != null &&
      seat !== HUMAN_SEAT;
    if (!botTurn) {
      setOppTurnClock(null);
      return undefined;
    }
    setOppTurnClock({ endAt: Date.now() + BOT_TURN_MS, totalMs: BOT_TURN_MS, seat });
    const iv = window.setInterval(() => setOppRingTick((x) => x + 1), 50);
    return () => {
      window.clearInterval(iv);
      setOppTurnClock(null);
    };
  }, [engineState.currentTurnSeat, engineState.phase, sessionStarted, setOppRingTick, setOppTurnClock, trickAnim]);

  useEffect(() => {
    if (!sessionStarted) return undefined;
    if (roundDealAnimActive) return undefined;
    if (trickAnim && (trickAnim.phase === 'reveal' || trickAnim.phase === 'collect')) return undefined;
    const s = engineState;
    if (s.phase === 'match_end') return undefined;

    if (s.phase === 'auction' && s.currentTurnSeat != null && s.currentTurnSeat !== HUMAN_SEAT) {
      const t = window.setTimeout(() => {
        const cur = stateRef.current;
        if (cur.phase !== 'auction' || cur.currentTurnSeat === HUMAN_SEAT) return;
        const cmd = pickBotAuctionCommand(cur, Math.random);
        if (!cmd) return;
        apply(cmd);
      }, BOT_TURN_MS);
      return () => window.clearTimeout(t);
    }

    if (s.phase === 'play' && s.currentTurnSeat != null && s.currentTurnSeat !== HUMAN_SEAT) {
      const t = window.setTimeout(() => {
        const cur = stateRef.current;
        if (cur.phase !== 'play' || cur.currentTurnSeat === HUMAN_SEAT) return;
        if (pendingPlayCmdRef.current) return;
        const cmd = pickBotPlayCmd(cur);
        if (!cmd) return;
        runBotPlayCommand(cmd, cur);
      }, BOT_TURN_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [
    apply,
    engineState,
    pendingPlayCmdRef,
    pickBotPlayCmd,
    roundDealAnimActive,
    runBotPlayCommand,
    sessionStarted,
    stateRef,
    trickAnim,
  ]);

  return {
    pickPlayCmdForSeat,
  };
}

